import React, {useEffect, useState} from 'react';
import {useRouter} from "next/router";
import {trackNavigation} from "helpers/analytics";
import {isEqual} from "lodash";
import useLooper from "./useLooper";
import {formatCityState} from "../utils";
import {capitalizeWords} from "helpers/string";

const sortOptions = [
    { sort_column: 'expire_date', sort_direction: 'asc', label: 'Expire Date: Soonest to Latest' },
    { sort_column: 'expire_date', sort_direction: 'desc', label: 'Expire Date: Latest to Soonest' },
    { sort_column: 'current_bid', sort_direction: 'desc', label: 'Current Bid: Highest to Lowest' },
    { sort_column: 'current_bid', sort_direction: 'asc', label: 'Current Bid: Lowest to Highest' },
    { sort_column: 'active_date', sort_direction: 'asc', label: 'Start Date: Soonest to Latest' },
    { sort_column: 'active_date', sort_direction: 'desc', label: 'Start Date: Latest to Soonest' },
    { sort_column: 'total_bids', sort_direction: 'asc', label: 'Total Bids: Least to Most' },
    { sort_column: 'total_bids', sort_direction: 'desc', label: 'Total Bids: Most to Least' },
    { sort_column: 'total_views', sort_direction: 'asc', label: 'Total Views: Least to Most' },
    { sort_column: 'total_views', sort_direction: 'desc', label: 'Total Views: Most to Least' },
];

const initialSearchState = {
    currentPage: 0,
    upcoming_current_page: 0,
    paginatedAuctions: [],
    active_tab: 'active',
    page_history: [
        {tab: 'active', page_num: 1},
    ],

    params_refill: {
        fill_vars: [
            'term',
            'term_state',
            'term_city',
            'filter_types',
            'filter_categories',
            'zipcode',
            'type',
            'radius',
            'filter_unit_contents',
            'page_num',
            'upcoming_page_num',
            'sort_column_index',
            'sort_column',
            'sort_direction'],
        fill_vars_update: [
            'term',
            'term_state',
            'term_city',
            'filter_unit_contents',
            'filter_types',
            'page_num',
            'sort_column_index',
            'sort_column',
            'sort_direction'],
    },

    search_obj: {
        sort_column_index: 0,
        page_num: 1,
        page_count: 15,
        upcoming_page_num: 1,
        type: 'zipcode',
        term: '',
        radius: '100',
        filter_types: ['1', '2', '3', '4'],
        filter_categories: [],
        sort_column: 'expire_date',
        sort_direction: 'asc',
    },

    search_obj_default: {
        sort_column_index: 0,
        page_num: 1,
        upcoming_page_num: 1,
        type: 'zipcode',
        term: '',
        radius: '100',
        filter_types: ['1', '2', '3'],
        filter_categories: [],
        sort_column: 'expire_date',
        sort_direction: 'asc',
        term_state_label: ''
    },
};

const getType = ({state, city}) => {
    if (city) return 'city'
    if (state) return 'state'
    return null
}

const useSearch = ({
    user_id,
    getOnlineAuctions,
    getUpcomingAuctions,
    initialRender
}) => {
    const router = useRouter();
    const [searchOnChange, setSearchOnChange] = useState(false);
    const [searchState, setSearchState] = useState({
        ...initialSearchState,
        search_obj: {
            ...initialSearchState.search_obj,
            ...JSON.parse(window.localStorage.getItem('search_obj')),
            type: getType(router?.query) || JSON.parse(window.localStorage.getItem('search_obj'))?.type,
            term_state: router?.query?.state,
            term_city: router?.query?.city && `${capitalizeWords(router?.query?.city)}, ${router?.query?.state}`,
        },
    })

    const makeApiObj = searchObj => {
        const search_obj_for_api = searchObj;

        if (typeof search_obj_for_api.filter_types === 'string') {
            search_obj_for_api.filter_types = [search_obj_for_api.filter_types];
        }

        if (!search_obj_for_api.filter_types || search_obj_for_api.filter_types.length === 0) {
            search_obj_for_api.filter_types = [searchState.search_obj_default.filter_types];
        }

        if (typeof search_obj_for_api.filter_categories === 'string') {
            search_obj_for_api.filter_categories = [search_obj_for_api.filter_categories];
        }

        if (search_obj_for_api.type === 'city' && search_obj_for_api.term_city) {
            search_obj_for_api.term = search_obj_for_api.term_city
        }

        return search_obj_for_api
    }

    const redoSearch = ({
        type: auction_type,
        search_obj_for_api: _search_obj_for_api,
        show_loading = true
    } = {}) => {
        const search_obj_for_api = makeApiObj(_search_obj_for_api || searchState.search_obj)

        trackNavigation({ user_id });

        switch (auction_type) {
            case 'upcoming':
                return getUpcomingAuctions(search_obj_for_api, { show_loading });
            case 'active':
                return getOnlineAuctions(search_obj_for_api, { show_loading });
            default:
                return Promise.all([
                    getOnlineAuctions(search_obj_for_api, { show_loading }),
                    getUpcomingAuctions(search_obj_for_api, { show_loading }),
                ]).catch((/* err */) => null);
        }
    };

    const {looper, restartLoop, startLooping, fireCancel} = useLooper({
        redoSearch,
        searchState
    });

    const searchObjUpdated = ({ target: { name, value, label } }) => new Promise((resolve) => {
        setSearchState({
            ...searchState,
            search_obj: { ...searchState.search_obj, [name]: value, [name + '_label']: label }
        });

        resolve();
    })

    const onSortByChange = async ({ target: { value: sort_column_index } }) => {
        const { sort_direction, sort_column } = sortOptions[Number(sort_column_index)];

        setSearchState({
            ...searchState,
            search_obj: {
                ...searchState.search_obj,
                sort_direction,
                sort_column,
                sort_column_index,
            },
        });
    };

    useEffect(() => {
        if (!isEqual(JSON.parse(window.localStorage.getItem('search_obj')), searchState.search_obj)) {
            window.localStorage.setItem('search_obj',
                JSON.stringify({
                    ...searchState.search_obj,
                })
            )
        }

        if (!initialRender.current) {
            window.localStorage.setItem('search_obj',
                JSON.stringify({
                    ...JSON.parse(window.localStorage.getItem('search_obj')),
                    ...searchState.search_obj,
                })
            )

            if (searchOnChange) restartLoop()
        }
    }, [searchState.search_obj])

    useEffect(() => restartLoop(), [
        searchState.search_obj.page_num,
        searchState.search_obj.upcoming_page_num,
        searchState.search_obj.page_count,
        searchState.search_obj.sort_column,
        searchState.search_obj.sort_direction,
        searchState.search_obj.sort_column_index,
    ])

    useEffect(() => {
        if (window) {
            if (window.localStorage.getItem('search_obj')) {
                const newSearchObj = {
                    ...JSON.parse(window.localStorage.getItem('search_obj')),
                };

                if (router?.query?.state && !router?.query?.city) {
                    newSearchObj.type = 'state';
                    newSearchObj.term_state = router?.query?.state;
                    newSearchObj.term_state_label = router?.query?.state;
                }

                if (router?.query?.city && router?.query?.city !== 'undefined') {
                    newSearchObj.type = 'city';
                    newSearchObj.term_city = formatCityState(`${capitalizeWords(router?.query?.city)}, ${router?.query?.state}`)
                }

                setSearchState({
                    ...searchState,
                    search_obj: newSearchObj,
                    search_obj_default: JSON.parse(window.localStorage.getItem('search_obj'))
                })
            } else {
                setSearchState({
                    ...searchState,
                    search_obj: {
                        ...searchState.search_obj,
                        ...JSON.parse(window.localStorage.getItem('search_obj')),
                        term_state: router?.query?.state,
                        //term_city: `${router?.query?.city}, ${router?.query?.state}`,
                    }
                })
            }
        }

        restartLoop()

        return () => fireCancel();
    }, [])

    return {
        searchState,
        setSearchState,
        sortOptions,
        looper,
        redoSearch,
        startLooping,
        restartLoop,
        searchObjUpdated,
        onSortByChange,
        fireCancel,
        initialSearchState,
        makeApiObj
    };
};

export default useSearch;
